import { Link, createFileRoute, useCanGoBack, useRouter } from '@tanstack/react-router'
import { useCallback, useState } from 'react'
import { useIsAdmin } from '../components/AdminBar'
import { OfferTable } from '../components/OfferTable'
import { RequestDialog } from '../components/RequestDialog'
import { ReviewScore, hasReviewScore } from '../components/ReviewScore'
import { useConfig, useGame } from '../lib/queries'
import { cue } from '../lib/sound'
import { useOpenAtTop } from '../lib/use-open-at-top'
import { initialsFromTitle, tintFromString } from '../lib/tint'

export const Route = createFileRoute('/game/$slug')({
  component: GameDetailPage,
})

const DAY_MS = 24 * 60 * 60 * 1000

function GameDetailPage() {
  const { slug } = Route.useParams()
  const game = useGame(slug)
  const config = useConfig()
  const isAdmin = useIsAdmin()
  const [requesting, setRequesting] = useState<{ keyId: string; label: string } | null>(null)

  // Arrived at from a catalog somebody had scrolled a long way down.
  useOpenAtTop(slug)

  if (game.isPending) return <DetailSkeleton />

  if (game.isError || !game.data) {
    return (
      <div className="grain flex min-h-dvh flex-col items-center justify-center gap-4 px-6 text-center">
        <h1 className="font-display text-2xl font-semibold">Game not found</h1>
        <p className="max-w-sm text-sm text-ink-soft">
          It may have been claimed and removed from the catalog.
        </p>
        <Link
          to="/"
          className="rounded-lg bg-accent px-4 py-2 text-sm font-semibold text-surface-0 transition-colors hover:bg-accent-strong"
        >
          Back to the library
        </Link>
      </div>
    )
  }

  const detail = game.data
  const expiryDays = detail.soonestExpiry
    ? Math.ceil((new Date(detail.soonestExpiry).getTime() - Date.now()) / DAY_MS)
    : null

  return (
    <div className="grain min-h-dvh">
      {/*
        Hero with a scrim, then the transparent wordmark over it — the two
        Steam assets that make a game page feel like a game page rather than a
        record in a table.

        Bounded by the same outer width as the rest of the site rather than the
        viewport. Steam's hero art is 1920 wide, so past that it is being
        upscaled, and on an ultrawide display it also stopped bearing any
        relation to the 1100px column of text beneath it.
      */}
      <header className="relative mx-auto w-full max-w-[1600px]">
        <div className="relative h-[34vh] min-h-[200px] w-full overflow-hidden sm:h-[42vh] sm:min-h-[280px]">
          <HeroArt url={detail.heroUrl} title={detail.title} />

          {/* Two washes of the page colour: one up from the bottom, which also
              keeps the wordmark legible over bright art, and one in from the
              sides once the art no longer reaches the screen edges. */}
          <div className="hero-fade-bottom pointer-events-none absolute inset-0" />
          <div className="hero-fade-sides pointer-events-none absolute inset-0" />
        </div>

        <BackLink siteName={config.data?.siteName ?? ''} />

        {/*
          Pulled up over the bottom of the hero. The floor height matters: a
          logo is up to 112px tall and a title is one line of text, and without
          it the shorter of the two dragged everything below it up under the
          artwork — which is what a game with no logo looked like.
        */}
        <div className="mx-auto -mt-24 flex min-h-24 w-full max-w-[1100px] items-end px-4 sm:-mt-32 sm:min-h-32 sm:px-8">
          {detail.logoUrl ? (
            <img
              src={detail.logoUrl}
              alt={detail.title}
              className="max-h-20 w-auto max-w-[min(100%,32rem)] object-contain object-left drop-shadow-[0_4px_24px_rgb(0_0_0/0.7)] sm:max-h-28"
            />
          ) : (
            <h1 className="font-display text-3xl font-semibold tracking-[-0.03em] drop-shadow-[0_4px_24px_rgb(0_0_0/0.7)] sm:text-5xl">
              {detail.title}
            </h1>
          )}
        </div>
      </header>

      <main id="main" className="mx-auto w-full max-w-[1100px] px-4 pt-6 pb-16 sm:px-8 sm:pt-8 sm:pb-20">
        <div className="grid gap-6 lg:grid-cols-[1fr_20rem] lg:gap-10">
          <div className="min-w-0">
            {/* The logo replaces the heading visually, but the page still
                needs the title in the document for screen readers and tabs. */}
            {detail.logoUrl ? <h1 className="sr-only">{detail.title}</h1> : null}

            <dl className="flex flex-wrap gap-x-6 gap-y-3 text-sm sm:gap-x-8">
              {detail.developer ? <Meta label="Developer" value={detail.developer} /> : null}
              {detail.publisher && detail.publisher !== detail.developer ? (
                <Meta label="Publisher" value={detail.publisher} />
              ) : null}
              {detail.releaseDate ? (
                <Meta label="Released" value={detail.releaseDate.slice(0, 4)} />
              ) : null}
              {hasReviewScore(detail.reviewPositive, detail.reviewCount) ? (
                <div>
                  <dt className="font-mono text-[10px] tracking-[0.16em] text-ink-muted uppercase">
                    Reviews
                  </dt>
                  <dd className="mt-1">
                    <ReviewScore positive={detail.reviewPositive} total={detail.reviewCount} />
                  </dd>
                </div>
              ) : null}
            </dl>

            {detail.summary ? (
              <p className="mt-6 max-w-prose text-sm leading-relaxed text-ink-soft">
                {detail.summary}
              </p>
            ) : null}

            {detail.genres.length > 0 ? (
              <ul className="mt-6 flex flex-wrap gap-2">
                {detail.genres.map((genre) => (
                  <li
                    key={genre}
                    className="rounded-full border border-line bg-surface-1 px-3 py-1 text-xs text-ink-soft"
                  >
                    {genre}
                  </li>
                ))}
              </ul>
            ) : null}

            {detail.offers.length > 0 ? (
              <div className="mt-8">
                <h2 className="mb-3 font-mono text-[10px] tracking-[0.16em] text-ink-muted uppercase">
                  {isAdmin ? 'Your keys' : 'Available keys'}
                </h2>
                <OfferTable
                  offers={detail.offers}
                  canAsk={!isAdmin && config.data?.requestsOpen !== false}
                  onAsk={(offer) =>
                    setRequesting({
                      keyId: offer.id,
                      label: `${offer.storeName}${offer.edition ? ` · ${offer.edition}` : ''}`,
                    })
                  }
                />
              </div>
            ) : null}

            {detail.storeLinks.length > 0 ? (
              <div className="mt-8">
                <h2 className="font-mono text-[10px] tracking-[0.16em] text-ink-muted uppercase">
                  Store pages
                </h2>
                <ul className="mt-3 flex flex-wrap gap-2">
                  {detail.storeLinks.map((link) => (
                    <li key={link.slug}>
                      <a
                        href={link.url}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="flex items-center gap-2 rounded-lg border border-line bg-surface-1 px-3 py-2 text-sm text-ink-soft transition-colors hover:border-line-strong hover:text-ink"
                      >
                        <span
                          aria-hidden
                          className="size-2 rounded-full"
                          style={{ backgroundColor: link.color }}
                        />
                        {link.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>

          <aside className="lg:sticky lg:top-8 lg:self-start">
            {/* Beside the count on a phone, above it on a desktop: a portrait
                cover at full column width is most of a phone screen, but at a
                thumbnail size it still says which game this is. */}
            <div className="flex items-start gap-4 rounded-xl border border-line bg-surface-1 p-5 lg:block">
              <Cover detail={detail} className="cover-compact w-24 shrink-0 lg:hidden" />

              <div className="min-w-0">
              <p className="font-mono text-3xl font-medium text-ink tabular-nums">
                {detail.availableKeys}
              </p>
              <p className="mt-0.5 text-sm text-ink-soft">
                {detail.availableKeys === 1 ? 'key available' : 'keys available'}
              </p>

              {expiryDays !== null && expiryDays <= 30 ? (
                <p className="mt-4 rounded-lg border border-warn/30 bg-warn/10 px-3 py-2 text-xs text-warn">
                  A key here expires in {expiryDays} {expiryDays === 1 ? 'day' : 'days'}.
                </p>
              ) : null}

              {isAdmin ? (
                <p className="mt-4 text-xs leading-relaxed text-ink-muted">
                  These are yours. Manage them from the admin library.
                </p>
              ) : config.data?.requestsOpen === false ? (
                <p className="mt-4 rounded-lg border border-line bg-surface-2 px-3 py-2 text-xs text-ink-soft">
                  Requests are paused at the moment.
                </p>
              ) : (
                <p className="mt-4 text-xs leading-relaxed text-ink-muted">
                  Pick the copy you want and ask. No account needed.
                </p>
              )}
              </div>
            </div>

            <Cover detail={detail} className="cover-full mt-4 hidden w-full lg:block" />
          </aside>
        </div>
      </main>

      {requesting ? (
        <RequestDialog
          gameId={detail.id}
          gameTitle={detail.title}
          keyId={requesting.keyId}
          keyLabel={requesting.label}
          onClose={() => setRequesting(null)}
        />
      ) : null}
    </div>
  )
}

/**
 * Getting back to where you were.
 *
 * When there is history to go back to, this steps back rather than linking to
 * `/` — the catalog keeps its search, filters and scroll position, which a
 * plain link to the index would throw away. Arriving from a shared link has no
 * history to return to, so that case falls back to a real link.
 *
 * Pinned over the hero with its own backdrop, because as plain text on top of
 * cover art it was effectively invisible.
 */
/**
 * The wide artwork, arriving without a flash.
 *
 * Two things were making one. The image had nothing behind it, so until the
 * bytes landed there was a hole where the art goes and then art — and it is a
 * 1920-wide JPEG, which is not instant even on a good connection. And it
 * appeared at full opacity the moment it decoded, so the change was a hard cut
 * rather than something happening.
 *
 * So it fades in once it has fully decoded, up from the page itself.
 *
 * Nothing is painted behind it. A tint was, on the reasoning that fading in
 * from nothing is worse than fading in from something — but that is a
 * light-background instinct. Here "nothing" is a near-black page, and the tint
 * was a mid-tone: it arrived as a pale frame, sat there for the length of the
 * download, and vanished. It replaced the flash it was meant to remove.
 *
 * A game with no wide art still gets the tint, because there the block is the
 * artwork rather than a placeholder for it.
 */
function HeroArt({ url, title }: { url: string | null; title: string }) {
  const [loaded, setLoaded] = useState(false)

  /*
   * A cached image can finish decoding before React attaches the handler, and
   * `onLoad` does not fire for one that was already complete — which would
   * leave the art invisible for exactly the case that should be fastest. The
   * callback ref checks on attach instead.
   */
  const watch = useCallback((node: HTMLImageElement | null) => {
    if (node?.complete) setLoaded(true)
  }, [])

  if (!url) return <div className="size-full" style={{ backgroundColor: tintFromString(title) }} />

  return (
    <img
      ref={watch}
      src={url}
      alt=""
      onLoad={() => setLoaded(true)}
      fetchPriority="high"
      className={`size-full object-cover object-center transition-opacity duration-500 ${
        loaded ? 'opacity-100' : 'opacity-0'
      }`}
    />
  )
}

function BackLink({ siteName }: { siteName: string }) {
  const router = useRouter()
  const canGoBack = useCanGoBack()

  const segment =
    'flex items-center gap-1.5 px-3 py-2 text-[13px] text-ink transition-colors hover:bg-white/10 sm:gap-2 sm:px-3.5 sm:text-sm'

  return (
    /*
     * Pinned over the hero, but inside the same column the page uses. Left to
     * the viewport edge it drifted off on its own on a wide screen, lining up
     * with nothing.
     */
    <div className="pointer-events-none absolute inset-x-0 top-4 z-10 sm:top-6">
      <div className="mx-auto w-full max-w-[1100px] px-4 sm:px-8">
        {/*
          One control rather than two floating bubbles: going back and going
          home are the same kind of move, and splitting them meant the site
          name had to be dropped on a phone to fit two pills side by side.
          Segmented, it stays readable at every width.
        */}
        {/*
          Every segment here leaves the page, so all three take the same cue as
          closing a sheet — whether that is stepping back through history, or
          the fallback straight to the library when there is no history to step
          through.
        */}
        <div className="pointer-events-auto inline-flex items-stretch overflow-hidden rounded-full border border-white/15 bg-black/75 shadow-lg">
          {canGoBack ? (
            <button
              type="button"
              onClick={() => {
                cue('dismissed')
                router.history.back()
              }}
              className={segment}
            >
              <span aria-hidden>←</span> Back
            </button>
          ) : (
            <Link to="/" onClick={() => cue('dismissed')} className={segment}>
              <span aria-hidden>←</span> Back
            </Link>
          )}

          <span aria-hidden className="my-1.5 w-px bg-white/15" />

          {/* Which site this is. A game page reached from a shared link opens
              on somebody else's artwork with nothing saying where they are. */}
          <Link to="/" onClick={() => cue('dismissed')} className={segment}>
            <img src="/favicon.svg" alt="" className="size-4 rounded sm:size-[1.125rem]" />
            <span className="font-display font-semibold tracking-tight">{siteName}</span>
          </Link>
        </div>
      </div>
    </div>
  )
}

/**
 * The portrait capsule, or the game's initials when there is no art.
 *
 * Which of the two instances continues the morph from the grid is decided in
 * CSS — see `.cover-compact` and `.cover-full` in the theme.
 */
function Cover({
  detail,
  className,
}: {
  detail: { capsuleUrl: string | null; title: string }
  className: string
}) {
  if (detail.capsuleUrl) {
    return (
      <img
        src={detail.capsuleUrl}
        alt=""
        className={`aspect-[2/3] rounded-lg border border-line object-cover ${className}`}
      />
    )
  }

  return (
    <div
      className={`flex aspect-[2/3] items-center justify-center rounded-lg ${className}`}
      style={{ backgroundColor: tintFromString(detail.title) }}
    >
      <span className="font-display text-3xl font-semibold text-white/25">
        {initialsFromTitle(detail.title)}
      </span>
    </div>
  )
}

function Meta({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-[11px] tracking-wider text-ink-muted uppercase">{label}</dt>
      <dd className="mt-0.5 text-ink">{value}</dd>
    </div>
  )
}

function DetailSkeleton() {
  return (
    <div className="grain min-h-dvh">
      <div className="h-[42vh] min-h-[280px] w-full animate-pulse bg-surface-1" />
      <div className="mx-auto w-full max-w-[1100px] px-6 pt-8 sm:px-8">
        <div className="h-8 w-64 animate-pulse rounded bg-surface-2" />
        <div className="mt-6 h-4 w-full max-w-prose animate-pulse rounded bg-surface-2" />
        <div className="mt-2 h-4 w-3/4 max-w-prose animate-pulse rounded bg-surface-2" />
      </div>
    </div>
  )
}
